import Image from 'next/image';
import { Text } from '../../components/Text';
import { AgentCardFragment } from './agentCard.generated';
import {
  PhoneIcon,
  ChatBubbleBottomCenterTextIcon,
} from '@heroicons/react/24/solid';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import Link from 'next/link';
import { Rating } from '../../components/Rating';
import { aggregateReviews } from '../../lib/reviewAggregation';
import { logEvent } from '../../lib/analytics';

interface AgentCardProps {
  agent: AgentCardFragment;
  aggregatedReviews?: ReturnType<typeof aggregateReviews>;
}

export const AgentCard: React.FC<AgentCardProps> = ({
  agent,
  aggregatedReviews,
}) => {
  return (
    <div className="grid grid-cols-[max-content,auto] gap-4 lg:gap-8">
      <div className="flex flex-col justify-between">
        {agent.photo && (
          <Link href={`/agents/${agent.slug}`}>
            <Image
              src={agent.photo.url ?? ''}
              alt={agent.photo.description ?? ''}
              width={110}
              height={110}
              className="rounded-full"
            />
          </Link>
        )}
        <div className="mt-2">
          <Text.Small>
            <Link
              href={`/agents/${agent.slug}#reviews`}
              className="text-blue-600 hover:text-blue-700"
            >
              <Rating stars={aggregatedReviews?.average ?? 0} showNumber />
              <p>
                {aggregatedReviews?.total ?? 0}{' '}
                {aggregatedReviews?.total === 1 ? 'Review' : 'Reviews'}
              </p>
            </Link>
          </Text.Small>
        </div>
      </div>
      <div className="flex flex-col justify-between">
        <div className="space-y-1">
          <Link href={`/agents/${agent.slug}`}>
            <Text.H4 className="text-gray-900 !font-medium">
              {agent.name}
            </Text.H4>
          </Link>
          <Text.Small className="!text-gray-500 font-medium !text-xs">
            Licensed since {agent.licensedYear}
          </Text.Small>
          <Text.Small className="!text-gray-500 font-medium !text-xs">
            {agent.city}, {agent.county} County
          </Text.Small>
        </div>
        <div className="space-y-2">
          <Text.Small>
            <a
              href={`tel:${agent.phoneNumber}`}
              className="text-blue-600 hover:text-blue-700"
              onClick={() =>
                logEvent({
                  category: 'Agent Directory',
                  name: 'Phone number click',
                  agentSlug: agent.slug,
                })
              }
            >
              <PhoneIcon className="w-4 h-4 inline mr-2" />
              {parsePhoneNumberFromString(
                `+${agent.phoneNumber}`,
              )?.formatNational()}
            </a>
          </Text.Small>
          <Text.Small>
            <a
              href={`sms:${agent.phoneNumber}`}
              className="text-blue-600 hover:text-blue-700"
              onClick={() =>
                logEvent({
                  category: 'Agent Directory',
                  name: 'SMS number click',
                  agentSlug: agent.slug,
                })
              }
            >
              <ChatBubbleBottomCenterTextIcon className="w-4 h-4 inline mr-2" />
              {parsePhoneNumberFromString(
                `+${agent.phoneNumber}`,
              )?.formatNational()}
            </a>
          </Text.Small>
          {agent.velocifyId && agent.active && (
            <Text.Small>
              <Link
                href={`/agents/${agent.slug}/quote`}
                className="text-blue-600 hover:text-blue-700"
                onClick={() =>
                  logEvent({
                    category: 'Agent Directory',
                    name: 'Quote link click',
                    agentSlug: agent.slug,
                  })
                }
              >
                Get Quote from {agent.name?.split(' ')[0]}
              </Link>
            </Text.Small>
          )}
        </div>
      </div>
    </div>
  );
};
